import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import Select from "components/common/Select";
import TableComponent from "components/common/TableComponent";
import PageLoader from "components/common/PageLoader";

import { getLeavePoliciesAPI, getAllCountriesLeavePolicies } from "services/useTimeoffService";

import {
  FlagIcon,
  ArrowIcon,
  formatDate,
  formatLeaveQuota,
  getEmployeesCount,
  getPoliciesCount,
  COUNTRY_DETAIL_TABLE_COLUMNS,
} from "components/Screens/Setting/utils";

import { COUNTRIES, COUNTRIES_LIST, COUNTRIES_URL_MAPPING } from "components/Screens/Setting/constants";
import { TIME_OFF_ROUTES } from "constants/routes";

import "./CountryDetails.scss";

function CountryDetails() {
  const { countryName } = useParams();
  const navigate = useNavigate();

  const [leavePolicies, setLeavePolicies] = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(true);
  const [policiesError, setPoliciesError] = useState(null);
  const [countriesData, setCountriesData] = useState([]);

  const selectedCountry = COUNTRIES[countryName];
  const countryOptions = COUNTRIES_LIST.map((country) => ({
    value: country,
    label: country,
  }));

  const handleCountryChange = (newCountry) => {
    navigate(TIME_OFF_ROUTES.COUNTRY_DETAIL(COUNTRIES_URL_MAPPING[newCountry]));
  };

  const handlePolicyClick = (policy) => {
    const policyTypeSlug = policy.type.toLowerCase().replace(/\s+/g, "-");
    navigate(TIME_OFF_ROUTES.POLICY_DETAIL(countryName, policyTypeSlug, policy.id));
  };

  useEffect(() => {
    const fetchCountriesData = async () => {
      const { data = [], success } = await getAllCountriesLeavePolicies();
      const formattedData = [];

      if (success && data) {
        data.forEach(({ policyCount = 0, employeeCount = 0, country }) => {
          formattedData.push({
            country,
            policyCount,
            employeeCount,
          });
        });
      }

      setCountriesData(formattedData);
    };

    fetchCountriesData();
  }, []);

  useEffect(() => {
    const fetchLeavePolicies = async () => {
      if (!selectedCountry) return;

      try {
        setPoliciesLoading(true);
        const response = await getLeavePoliciesAPI(selectedCountry);
        const formattedPolicies = response?.data?.policies.map(
          ({ id, leaveType, leaveQuota, createdAt, updatedAt, createdBy }) => ({
            id,
            type: leaveType,
            entitledDays: formatLeaveQuota(leaveQuota, leaveType),
            createdOn: formatDate(createdAt),
            lastUpdatedOn: formatDate(updatedAt),
            createdBy: createdBy || "Admin",
          })
        );

        setLeavePolicies(formattedPolicies);
      } catch (err) {
        setPoliciesError(err.message);
      } finally {
        setPoliciesLoading(false);
      }
    };

    fetchLeavePolicies();
  }, [selectedCountry]);

  useEffect(() => {
    if (!countryName) {
      const firstCountryKey = Object.keys(COUNTRIES)[0];
      navigate(TIME_OFF_ROUTES.COUNTRY_DETAIL(firstCountryKey));
    }
  }, [countryName, navigate]);

  if (policiesLoading) {
    return <PageLoader />;
  }
  if (policiesError) {
    return <div>Error: {policiesError}</div>;
  }

  return (
    <div className="bg-white w-100p">
      <div className="country-details-header">
        <div className="country-select-wrapper">
          <Select
            name="countrySelect"
            label=""
            options={countryOptions}
            value={selectedCountry}
            onChange={handleCountryChange}
            startIcon={<FlagIcon country={selectedCountry} />}
            endIcon={<ArrowIcon />}
            placeholder="Select a country"
            fontSize="font-semibold"
            size="xs"
          />
          <div className="country-employees">
            {getPoliciesCount(countriesData, selectedCountry)} policies (applies to{" "}
            {getEmployeesCount(countriesData, selectedCountry)} employees)
          </div>
        </div>
      </div>

      <div className="country-details-table pl-1 pr-1">
        <TableComponent
          disableSort
          columns={COUNTRY_DETAIL_TABLE_COLUMNS}
          data={leavePolicies}
          clientSidePagination
          initialRowsPerPage={10}
          noBorderForSingleRow
          tableHeadSmall
          onRowClick={handlePolicyClick}
        />
      </div>
    </div>
  );
}

export default CountryDetails;
